import type {
  Application,
  ApplicationBranding,
  ApplicationConfigInput,
} from "./api";

export const REDIRECT_CORS_COPY =
  "Redirect URIs are exact authorization callbacks. They are separate from Project CORS browser origins; configuring one never configures the other.";

export const GOOGLE_STATUS_COPY =
  "Google sign-in is not configured. Provider credentials are write-only and will never be shown after save when Google setup is enabled.";

export const SCOPE_COPY = {
  profile: "Read safe basic profile fields",
  email: "Read email and email-verification status",
  offline_access: "Request refresh-capable access",
} as const;

export type ApplicationScope = keyof typeof SCOPE_COPY;

export type ApplicationFormValues = {
  slug: string;
  name: string;
  logoUrl: string;
  logoDarkUrl: string;
  description: string;
  developerName: string;
  developerUrl: string;
  homepageUrl: string;
  privacyPolicyUrl: string;
  termsUrl: string;
  supportContact: string;
  redirectUris: string;
  declaredDomains: string;
  allowedScopes: ApplicationScope[];
  branding: ApplicationBranding;
  accessTokenTTLSeconds: number;
  refreshTokenTTLSeconds: number;
  sessionTTLSeconds: number;
};

export type ApplicationFormResult = {
  input: ApplicationConfigInput | null;
  errors: Record<string, string>;
};

export function emptyApplicationForm(): ApplicationFormValues {
  return {
    slug: "",
    name: "",
    logoUrl: "",
    logoDarkUrl: "",
    description: "",
    developerName: "",
    developerUrl: "",
    homepageUrl: "",
    privacyPolicyUrl: "",
    termsUrl: "",
    supportContact: "",
    redirectUris: "",
    declaredDomains: "",
    allowedScopes: ["profile"],
    branding: {
      // primary (#964900) keeps white label contrast; container orange is UI chrome
      primary_color: "#964900",
      accent_color: "#0054CA",
      background_color: "#FFFFFF",
      text_color: "#1A1C1C",
      display_mode: "card",
    },
    accessTokenTTLSeconds: 900,
    refreshTokenTTLSeconds: 604800,
    sessionTTLSeconds: 604800,
  };
}

export function applicationToForm(application: Application): ApplicationFormValues {
  return {
    slug: application.slug,
    name: application.name,
    logoUrl: application.logo_url ?? "",
    logoDarkUrl: application.logo_dark_url ?? "",
    description: application.description ?? "",
    developerName: application.developer_name ?? "",
    developerUrl: application.developer_url ?? "",
    homepageUrl: application.homepage_url ?? "",
    privacyPolicyUrl: application.privacy_policy_url ?? "",
    termsUrl: application.terms_url ?? "",
    supportContact: application.support_contact ?? "",
    redirectUris: application.redirect_uris.join("\n"),
    declaredDomains: application.declared_domains.join("\n"),
    allowedScopes: [...application.allowed_scopes],
    branding: { ...application.branding },
    accessTokenTTLSeconds: application.security.access_token_ttl_seconds,
    refreshTokenTTLSeconds: application.security.refresh_token_ttl_seconds,
    sessionTTLSeconds: application.security.session_ttl_seconds,
  };
}

export function buildApplicationInput(values: ApplicationFormValues): ApplicationFormResult {
  const errors: Record<string, string> = {};
  const name = values.name.trim();
  const slug = values.slug.trim().toLowerCase();
  if (!name || name.length > 100) errors.name = "Name is required (maximum 100 characters).";
  if (!/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(slug)) {
    errors.slug = "Use 1-64 lowercase letters, numbers, and hyphens.";
  }
  if (values.description.trim().length > 1000) errors.description = "Maximum 1000 characters.";
  if (values.developerName.trim().length > 100) errors.developerName = "Maximum 100 characters.";
  if (values.supportContact.length > 254 || /[\r\n]/.test(values.supportContact)) {
    errors.supportContact = "Use one line with at most 254 characters.";
  }

  const presentationURLs = [
    ["logoUrl", values.logoUrl],
    ["logoDarkUrl", values.logoDarkUrl],
    ["developerUrl", values.developerUrl],
    ["homepageUrl", values.homepageUrl],
    ["privacyPolicyUrl", values.privacyPolicyUrl],
    ["termsUrl", values.termsUrl],
  ] as const;
  for (const [field, value] of presentationURLs) {
    if (value.trim() && !isSafeHTTPURL(value, false)) {
      errors[field] = "Use HTTPS, or explicit-port localhost HTTP for development; no userinfo, wildcard, or fragment.";
    }
  }

  const redirects = splitLines(values.redirectUris);
  if (redirects.length === 0 || redirects.length > 20) {
    errors.redirectUris = "Register 1-20 exact callback URLs.";
  } else if (redirects.some((value) => !isSafeHTTPURL(value, false))) {
    errors.redirectUris = "Every callback must be an exact safe HTTP(S) URL without a fragment or wildcard.";
  } else if (hasNormalizedDuplicate(redirects)) {
    errors.redirectUris = "Duplicate callback after URL normalization.";
  }

  const domains = splitLines(values.declaredDomains);
  if (domains.length > 20) {
    errors.declaredDomains = "Register at most 20 exact origins.";
  } else if (domains.some((value) => !isSafeHTTPURL(value, true))) {
    errors.declaredDomains = "Domains must be exact safe origins without paths, queries, or fragments.";
  } else if (hasNormalizedDuplicate(domains)) {
    errors.declaredDomains = "Duplicate domain after URL normalization.";
  }

  if (values.allowedScopes.length === 0) errors.allowedScopes = "Select at least one scope.";
  if (new Set(values.allowedScopes).size !== values.allowedScopes.length) {
    errors.allowedScopes = "Scopes must be unique.";
  }
  const colors = Object.entries(values.branding).filter(([key]) => key.endsWith("_color"));
  if (colors.some(([, value]) => !/^#[0-9a-fA-F]{6}$/.test(value))) {
    errors.branding = "Brand colors must use #RRGGBB.";
  } else if (contrastRatio(values.branding.text_color, values.branding.background_color) < 4.5) {
    errors.branding = "Text and background must meet WCAG AA contrast.";
  } else if (contrastRatio("#ffffff", values.branding.primary_color) < 4.5) {
    errors.branding = "Primary action color must contrast with white text.";
  }
  if (values.branding.display_mode !== "card" && values.branding.display_mode !== "centered") {
    errors.branding = "Display mode must be card or centered.";
  }
  validateBoundedInteger(errors, "accessTokenTTLSeconds", values.accessTokenTTLSeconds, 300, 3600);
  validateBoundedInteger(errors, "refreshTokenTTLSeconds", values.refreshTokenTTLSeconds, 3600, 2592000);
  validateBoundedInteger(errors, "sessionTTLSeconds", values.sessionTTLSeconds, 3600, 2592000);

  if (Object.keys(errors).length > 0) return { input: null, errors };
  return {
    errors,
    input: {
      slug,
      name,
      logo_url: values.logoUrl.trim(),
      logo_dark_url: values.logoDarkUrl.trim(),
      description: values.description.trim(),
      developer_name: values.developerName.trim(),
      developer_url: values.developerUrl.trim(),
      homepage_url: values.homepageUrl.trim(),
      privacy_policy_url: values.privacyPolicyUrl.trim(),
      terms_url: values.termsUrl.trim(),
      support_contact: values.supportContact.trim(),
      redirect_uris: redirects,
      declared_domains: domains,
      allowed_scopes: [...values.allowedScopes],
      branding: {
        ...values.branding,
        primary_color: values.branding.primary_color.toLowerCase(),
        accent_color: values.branding.accent_color.toLowerCase(),
        background_color: values.branding.background_color.toLowerCase(),
        text_color: values.branding.text_color.toLowerCase(),
      },
      security: {
        pkce_required: true,
        access_token_ttl_seconds: values.accessTokenTTLSeconds,
        refresh_token_ttl_seconds: values.refreshTokenTTLSeconds,
        session_ttl_seconds: values.sessionTTLSeconds,
      },
    },
  };
}

export function hasDangerousApplicationChanges(before: Application, after: ApplicationConfigInput): boolean {
  return (
    !sameStringSet(before.redirect_uris, after.redirect_uris) ||
    !sameStringSet(before.declared_domains, after.declared_domains ?? []) ||
    !sameStringSet(before.allowed_scopes, after.allowed_scopes ?? [])
  );
}

function splitLines(value: string): string[] {
  return value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
}

function validateBoundedInteger(
  errors: Record<string, string>,
  field: string,
  value: number,
  minimum: number,
  maximum: number,
) {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    errors[field] = `Use a whole number from ${minimum} to ${maximum}.`;
  }
}

function isSafeHTTPURL(raw: string, originOnly: boolean): boolean {
  const value = raw.trim();
  if (!value || value.includes("*") || value.includes("\\")) return false;
  const authority = value.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/([^/?#]+)/)?.[1];
  if (!authority || !/^[\x00-\x7F]+$/.test(authority)) return false;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
    if (parsed.username || parsed.password || parsed.hash) return false;
    if (!/^[\x00-\x7F]+$/.test(parsed.hostname) || parsed.hostname.endsWith(".")) return false;
    const loopback = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
    const hasExplicitPort = /:\d+$/.test(authority);
    if (parsed.protocol === "http:" && (!loopback || !hasExplicitPort)) return false;
    if (originOnly && (parsed.pathname !== "/" || parsed.search)) return false;
    return true;
  } catch {
    return false;
  }
}

function hasNormalizedDuplicate(values: string[]): boolean {
  const normalized = values.map((value) => new URL(value).toString());
  return new Set(normalized).size !== normalized.length;
}

function sameStringSet(left: readonly string[], right: readonly string[]): boolean {
  return [...left].sort().join("\n") === [...right].sort().join("\n");
}

function contrastRatio(first: string, second: string): number {
  const left = relativeLuminance(first);
  const right = relativeLuminance(second);
  return (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05);
}

function relativeLuminance(color: string): number {
  const channels = [color.slice(1, 3), color.slice(3, 5), color.slice(5, 7)].map(
    (value) => Number.parseInt(value, 16) / 255,
  );
  const linear = channels.map((value) =>
    value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}
